import { useCallback, useEffect, useState } from 'react';

/**
 * Fullscreen and install, the two things that turn a browser tab into
 * something worth playing on a phone.
 *
 * Both are best-effort by nature: iOS Safari has no element fullscreen API at
 * all (it gets there by being installed to the home screen instead), and the
 * install prompt only exists on engines that fire `beforeinstallprompt`. Each
 * hook reports whether it is usable so the UI can hide a control that would do
 * nothing rather than offer a button that silently fails.
 */

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

interface FullscreenDocument extends Document {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
}

function fullscreenElement(): Element | null {
  const doc = document as FullscreenDocument;
  return doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
}

export function useFullscreen(): { supported: boolean; active: boolean; toggle: () => void } {
  const [active, setActive] = useState(false);

  const supported =
    typeof document !== 'undefined' &&
    (typeof document.documentElement.requestFullscreen === 'function' ||
      typeof (document.documentElement as FullscreenElement).webkitRequestFullscreen === 'function');

  useEffect(() => {
    const onChange = (): void => setActive(fullscreenElement() !== null);
    onChange();
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const toggle = useCallback(() => {
    const doc = document as FullscreenDocument;
    const el = document.documentElement as FullscreenElement;
    try {
      if (fullscreenElement()) {
        void (doc.exitFullscreen?.() ?? doc.webkitExitFullscreen?.());
      } else {
        // The whole document, not the canvas: fullscreening the canvas alone
        // would take the HUD and every panel off screen with it.
        const request = el.requestFullscreen?.({ navigationUI: 'hide' }) ?? el.webkitRequestFullscreen?.();
        void Promise.resolve(request).then(lockLandscape).catch((err) => {
          console.warn('[pwa] fullscreen request rejected', err);
        });
      }
    } catch (err) {
      console.warn('[pwa] fullscreen request rejected', err);
    }
  }, []);

  return { supported, active, toggle };
}

interface LockableOrientation extends ScreenOrientation {
  lock?: (orientation: string) => Promise<void>;
}

/**
 * Asks the device to stay in landscape.
 *
 * Only ever a request. Chrome on Android honours it while the page is
 * fullscreen and rejects it otherwise, which is exactly why it is called from
 * the fullscreen path; iOS Safari has no orientation lock at all and gets
 * there through the manifest's `"orientation": "landscape"` when installed,
 * or through the rotate prompt when not. A rejection is normal and not worth
 * a warning in the console.
 */
export function lockLandscape(): void {
  if (typeof screen === 'undefined') return;
  const orientation = screen.orientation as LockableOrientation | undefined;
  try {
    void orientation?.lock?.('landscape').catch(() => undefined);
  } catch {
    /* Unsupported: the prompt in the UI is the fallback. */
  }
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** True when the page is already running as an installed app. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: fullscreen)').matches === true;
}

/**
 * Captures the browser's install prompt so it can be offered from the game's
 * own menu.
 *
 * The event is only fired once and only when the browser considers the app
 * installable (manifest, icons, a service worker with a fetch handler), and it
 * must be re-shown from a user gesture — hence stashing the event rather than
 * prompting on arrival, which browsers ignore anyway.
 */
export function useInstallPrompt(): { available: boolean; installed: boolean; promptInstall: () => void } {
  const [event, setEvent] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const onPrompt = (e: Event): void => {
      e.preventDefault();
      setEvent(e as InstallPromptEvent);
    };
    const onInstalled = (): void => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(() => {
    if (!event) return;
    void event.prompt().then(() => setEvent(null)).catch((err) => {
      console.warn('[pwa] install prompt failed', err);
    });
  }, [event]);

  return { available: event !== null && !installed, installed, promptInstall };
}

/**
 * Registers the offline service worker.
 *
 * Production only, and deliberately so: a worker caching assets in front of
 * the dev server is a very effective way to spend an afternoon looking at a
 * stale shader. `sw.js` lives in `public/` so it is served from the origin
 * root, which is the scope it needs to control the whole app.
 */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  if (!import.meta.env.PROD) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).catch((err) => {
      console.warn('[pwa] service worker registration failed', err);
    });
  });
}
